<template>
  <div class="flex o-x-scroll scroll-hide ws-nowrap  gap-thin">
    <!-- All Filters Button -->
    <button
      @click="showAllFilters = true"
      class="pd-small radius-medium bg-light flex-v-center flex gap-micro cursor-pointer"
      :class="{ 'bg-main': activeFiltersCount > 0 }"
    >
      <IconFilter class="i-regular" />
      <span class="h-1r"></span>
      <span v-if="activeFiltersCount">{{ activeFiltersCount }}</span>
    </button>

    <!-- Individual Filter Buttons -->
    <button
      v-for="filter in filters"
      :key="filter.value"
      @click="openFilter(filter.value)"
      class="pd-small radius-medium bg-light cursor-pointer flex-v-center flex gap-micro"
      :class="{ 'selected bg-main': isFilterActive(filter) }"
    >
      <IconCalendar v-if="filter.type === 'date'" class="mn-r-micro i-regular" />
      <span class="ws-nowrap">{{ filter.type === 'date' && getFilterValue(filter) ? formatFilterValue(filter) : filter.title }}</span>
      <span v-if="getFilterValue(filter) && filter.type !== 'date'" class="mn-l-micro">
        {{ formatFilterValue(filter) }}
      </span>
    </button>

    <!-- All Filters Popup -->
    <Popup
      :isPopupOpen="showAllFilters"
      @close-popup="closeAllFilters"
      :align="isPhone() ? 'bottom center' : 'center center'"
      class="w-clamp-20r-20r-100 bg-white radius-medium mobile:radius-zero mobile:radius-tr-medium mobile:radius-tl-medium pd-medium"
    >
      <FiltersGroup
        :filters="filters"
        v-model:selected="tempSelected"
        :immediate="false"
        :showHeader="true"
        :showApplyButton="true"
        :showResetButton="true"
        @update:selected="applyAllFilters"
      />
    </Popup>

    <!-- Individual Filter Popups -->
    <Popup
      v-for="filter in filters"
      :key="`popup-${filter.value}`"
      :isPopupOpen="individualPopups[filter.value]"
      @close-popup="cancelFilter(filter.value)"
      :align="isPhone() ? 'bottom center' : 'center center'"
      class="bg-white radius-medium w-clamp-20r-20r-100 mobile:radius-zero mobile:radius-tr-medium mobile:radius-tl-medium pd-medium"
    >
      <h4 class="mn-b-medium">{{ filter.title }}</h4>

      <!-- Checkbox Filter -->
      <FilterCheckbox
        v-if="filter.type === 'checkbox'"
        v-model="tempSelected[filter.value]"
        :options="filter.options"
      />

      <!-- Range Filter -->
      <FilterRange
        v-else-if="filter.type === 'range'"
        v-model="tempSelected[filter.value]"
        :minPlaceholder="filter.minPlaceholder || 'Min'"
        :maxPlaceholder="filter.maxPlaceholder || 'Max'"
        :label="filter.label"
      />

      <!-- Date Filter -->
      <div v-else-if="filter.type === 'date'">
        <div class="mn-t-small">
          <Calendar
            v-model:date="tempSelected[filter.value]"
            :allowRange="true"
            :disablePastDates="true"
            class="bg-light radius-small"
          />
        </div>
      </div>

      <!-- Radio/Options Filter -->
      <FilterOptions
        v-else-if="filter.type === 'radio'"
        v-model="tempSelected[filter.value]"
        :options="filter.options"
      />

      <div class="flex gap-thin mn-t-medium">
        <button
          @click="cancelFilter(filter.value)"
          class="button--light button flex-child-full"
        >
          Cancel
        </button>
        <button
          @click="applyFilter(filter.value)"
          class="button--main w-100 button flex-child-full"
        >
          Apply
        </button>
      </div>
    </Popup>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue'
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js'
import Popup from '@martyrs/src/design-system/components/Popup/Popup.vue'
import Calendar from '@martyrs/src/design-system/components/Calendar/Calendar.vue'
import FiltersGroup from './filters/FiltersGroup.vue'
import FilterCheckbox from './filters/FilterCheckbox.vue'
import FilterRange from './filters/FilterRange.vue'
import FilterDateRange from './filters/FilterDateRange.vue'
import FilterOptions from './filters/FilterOptions.vue'
import IconFilter from '@martyrs/src/modules/icons/navigation/IconFilter.vue'
import IconCross from '@martyrs/src/modules/icons/navigation/IconCross.vue'
import IconCalendar from '@martyrs/src/modules/icons/entities/IconCalendar.vue'

const filters = defineModel('filters', {
  type: Array,
  required: true
})

const selected = defineModel('selected', {
  type: Object,
  default: () => ({})
})

const emit = defineEmits(['select'])

const { formatDate, returnCurrency } = useGlobalMixins()

// State
const showAllFilters = ref(false)
const individualPopups = reactive({})
const tempSelected = reactive({})
const tempDateRange = ref(null)

// Initialize popups and temp values
watch(filters, (newFilters) => {
  newFilters.forEach(filter => {
    individualPopups[filter.value] = false
    
    if (!tempSelected[filter.value]) {
      if (filter.type === 'checkbox') {
        tempSelected[filter.value] = [...(selected.value[filter.value] || [])]
      } else if (filter.type === 'range') {
        tempSelected[filter.value] = { ...(selected.value[filter.value] || { min: '', max: '' }) }
      } else if (filter.type === 'date') {
        tempSelected[filter.value] = selected.value[filter.value] || null
      } else {
        tempSelected[filter.value] = selected.value[filter.value] || null
      }
    }
  })
}, { immediate: true, deep: true })

// Sync selected to tempSelected
watch(selected, (newSelected) => {
  Object.keys(newSelected).forEach(key => {
    const filter = filters.value.find(f => f.value === key)
    if (filter) {
      if (filter.type === 'checkbox') {
        tempSelected[key] = [...(newSelected[key] || [])]
      } else if (filter.type === 'range') {
        tempSelected[key] = { ...(newSelected[key] || { min: '', max: '' }) }
      } else {
        tempSelected[key] = newSelected[key]
      }
    }
  })
}, { deep: true })

// Computed
const activeFiltersCount = computed(() => {
  return Object.entries(selected.value).filter(([key, value]) => {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object' && value !== null) {
      return value.min || value.max
    }
    return value !== null && value !== undefined
  }).length
})

// Methods
const openFilter = (filterValue) => {
  individualPopups[filterValue] = true
}

const isFilterActive = (filter) => {
  const value = selected.value[filter.value]
  if (!value) return false
  if (Array.isArray(value)) return value.length > 0
  if (filter.type === 'range') return value.min || value.max
  return true
}

const getFilterValue = (filter) => {
  const value = selected.value[filter.value]
  if (!value) return false
  
  if (filter.type === 'range') {
    return value.min || value.max
  }
  
  if (filter.type === 'date') {
    return value && value.start && value.end
  }
  
  if (Array.isArray(value)) {
    return value.length > 0
  }
  
  return value
}

const formatFilterValue = (filter) => {
  const value = selected.value[filter.value]
  if (!value) return ''
  
  if (Array.isArray(value)) {
    return `(${value.length})`
  }
  
  if (filter.type === 'range') {
    if (!value.min && !value.max) return ''
    return `${value.min || '0'}-${value.max || '∞'}`
  }
  
  if (filter.type === 'date') {
    if (!value || !value.start || !value.end) return ''
    return `${formatDate(value.start, { dayMonth: true, language: 'en' })} - ${formatDate(value.end, { dayMonth: true, language: 'en' })}`
  }
  
  if (filter.type === 'radio') {
    const option = filter.options.find(o => o.value === value)
    return option ? `(${option.label})` : ''
  }
  
  return ''
}

const applyFilter = (filterValue) => {
  selected.value[filterValue] = tempSelected[filterValue]
  individualPopups[filterValue] = false
  emit('select', { filter: filterValue, value: tempSelected[filterValue] })
}

const cancelFilter = (filterValue) => {
  const filter = filters.value.find(f => f.value === filterValue)
  if (filter) {
    if (filter.type === 'checkbox') {
      tempSelected[filterValue] = [...(selected.value[filterValue] || [])]
    } else if (filter.type === 'range') {
      tempSelected[filterValue] = { ...(selected.value[filterValue] || { min: '', max: '' }) }
    } else if (filter.type === 'date') {
      tempSelected[filterValue] = selected.value[filterValue] || null
    } else {
      tempSelected[filterValue] = selected.value[filterValue] || null
    }
  }
  individualPopups[filterValue] = false
}

const applyAllFilters = (newValues) => {
  Object.entries(newValues).forEach(([key, value]) => {
    if (selected.value[key] !== value) {
      selected.value[key] = value
      emit('select', { filter: key, value })
    }
  })
  showAllFilters.value = false
}

const closeAllFilters = () => {
  showAllFilters.value = false
}
</script>

<style scoped>
.filters-content {
  max-height: 60vh;
  overflow-y: auto;
}
</style>